import React, { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useFeatureFlagWithPrefs } from '@fs/zion-flags'
import { type PID } from '@fs/zion-tree-types'
import { useParams } from '@fs/zion-router'
import { emptyUseContributionsReturn, useContributions } from '../changelog/lib/useContributions'

type ContributionsContextState = ReturnType<typeof useContributions>

const ContributionContext = createContext<ContributionsContextState | undefined>(undefined)

export const useContributionsContext = (): ContributionsContextState => {
  const context = useContext(ContributionContext)

  if (context === undefined) {
    throw new Error('useContributionsContext must be used within a ContributionsProvider')
  }

  return context
}

type ContributionsProviderProps = {
  children: ReactNode
  pageSize?: number
}

function ContributionLiveProvider({ children, pageSize }: ContributionsProviderProps): JSX.Element {
  const value = useContributions({ pageSize })
  return <ContributionContext.Provider value={value}>{children}</ContributionContext.Provider>
}

function ContributionNotLiveProvider({ children }: Omit<ContributionsProviderProps, 'pageSize'>): JSX.Element {
  const { pid } = useParams() as { pid: PID }
  const emptyValue = useMemo(() => ({ ...emptyUseContributionsReturn, pid }), [pid])
  return <ContributionContext.Provider value={emptyValue}>{children}</ContributionContext.Provider>
}

export default function ContributionsProvider({ children, pageSize }: ContributionsProviderProps): JSX.Element {
  const { data: contributionsFlag } = useFeatureFlagWithPrefs(
    'treeWeb_personR9_contributionChangeLog',
    {},
    { userPreferences: ['fslabs.labs'] }
  )

  if (contributionsFlag?.isOn) {
    return <ContributionLiveProvider pageSize={pageSize}>{children}</ContributionLiveProvider>
  }

  return <ContributionNotLiveProvider>{children}</ContributionNotLiveProvider>
}
